// router-basename.ts — deriva o `basename` do react-router pros DOIS mundos.
//
// PROBLEMA (empacotado file://):
// No web (http/https) o app é servido da raiz do host, então `location.pathname`
// começa em `/` e as rotas absolutas (`/home`, `/movies/:id`, ...) casam direto.
// Já num .ipk/.wgt (webOS/Tizen), o WebView carrega o index.html via `file://`
// a partir do diretório de instalação do app, ex.:
//   file:///media/developer/apps/usr/palm/applications/com.swipetv.tv/index.html
// Aí `location.pathname` é `.../com.swipetv.tv/index.html` — nenhuma rota `/home`
// casa, o router cai no ErrorBoundary e mostra "404 Not Found". O app boota mas
// não navega.
//
// SOLUÇÃO (decisão travada — basename derivado, NÃO HashRouter):
// Passamos `basename` = o pathname COMPLETO do index.html (incluindo o arquivo)
// ao `createBrowserRouter`. Por quê o arquivo e não só o diretório: o react-router
// faz `stripBasename(pathname, basename)` e casa o RESTO contra as rotas. Se o
// basename fosse só o diretório (`.../com.swipetv.tv`), o resto seria `/index.html`
// — que não é rota nenhuma → 404 (foi exatamente o bug observado na TV via CDP).
// Com o basename = pathname inteiro (`.../com.swipetv.tv/index.html`), o resto na
// carga inicial é `''` → o router normaliza pra `/` → casa a rota `/`. E a
// navegação interna (pushState pra `basename + /home`) deixa o pathname em
// `.../index.html/home`; ao resolver, o basename é removido e sobra `/home` → casa.
// Validado na TV real por CDP com o próprio stripBasename() do react-router:
//   basename=diretório  → resto '/index.html' (NÃO casa)
//   basename=completo   → resto '/' (casa) e '/home' após navegar (casa).
// As URLs do WEB continuam `/home` (sem `#`) — nada regride.
//
// GENÉRICO POR DESIGN: o path de instalação varia por device/OS/fabricante
// (webOS dev vs prod, Tizen, path do usuário). NUNCA hardcodamos um path — o
// basename sai do runtime (`window.location`). Funciona em qualquer TV e no web.
//
// Função pura + testável: recebe um `Location`-like (protocol + pathname) e
// devolve o basename. `deriveRouterBasename()` lê o `window.location` real.

/** Subconjunto de `window.location` que precisamos — facilita testar sem DOM. */
export interface LocationLike {
  protocol: string
  pathname: string
}

/**
 * Deriva o basename do router a partir de um location-like.
 *
 * Regras:
 *  - http/https (e qualquer protocolo servido por host): basename '/' — WEB não
 *    regride. As URLs continuam absolutas a partir da raiz do host.
 *  - file: (empacotado webOS/Tizen): basename = o pathname COMPLETO do index.html
 *    (incluindo o arquivo). Assim `stripBasename` deixa `''` → `/` na carga
 *    inicial e as rotas casam. NÃO o diretório (isso deixaria `/index.html` e
 *    daria 404 — bug confirmado na TV).
 *
 * Robustez:
 *  - Normaliza barras duplicadas e remove trailing slash (react-router quer o
 *    basename SEM barra final, exceto o próprio '/').
 *  - Se o pathname file:// terminar em '/' (diretório, sem arquivo explícito),
 *    usa o próprio diretório como basename — o resto já seria '/' de qualquer forma.
 *  - Nunca retorna string vazia: o mínimo é '/'.
 *  - Tolera pathname vazio/estranho — cai em '/' (comportamento web, seguro).
 *
 * @param loc protocol (ex: 'http:', 'file:') + pathname (ex: '/a/b/index.html')
 */
export function deriveRouterBasename(loc: LocationLike): string {
  const protocol = (loc.protocol || '').toLowerCase()
  const rawPath = loc.pathname || '/'

  // WEB (servido por host): basename da raiz. Cobre http/https. Não regride nada.
  // Só o mundo file:// precisa do basename derivado do path de instalação.
  if (protocol === 'http:' || protocol === 'https:') {
    return '/'
  }

  // EMPACOTADO (file:// e afins): o basename é o pathname INTEIRO do documento
  // (incluindo `index.html`). Ver o cabeçalho deste arquivo — é o pathname
  // completo que faz o resto virar '/' e as rotas casarem. Diretório NÃO serve.
  return normalizeBasename(rawPath)
}

/**
 * Normaliza um caminho em basename válido pro react-router:
 *  - garante barra inicial;
 *  - colapsa barras duplicadas (`//` → `/`);
 *  - remove barra final (react-router NÃO quer trailing slash), exceto a raiz '/'.
 */
function normalizeBasename(path: string): string {
  let out = path || '/'
  if (!out.startsWith('/')) out = `/${out}`
  out = out.replace(/\/{2,}/g, '/')
  if (out.length > 1) out = out.replace(/\/+$/, '')
  return out === '' ? '/' : out
}

/**
 * Deriva o basename do `window.location` real. Guard de SSR/teste-node: sem
 * `window`, devolve '/' (basename web seguro — servidor não é empacotado).
 */
export function deriveRouterBasenameFromWindow(): string {
  if (typeof window === 'undefined' || !window.location) return '/'
  return deriveRouterBasename(window.location)
}
